// =============================================================================
// SIREPPAR - Panel lateral "Intimaciones y observaciones" de los informes
// =============================================================================
// Lo que originalmente vivía dentro de ModalInformeSimple (circuito de "no
// presentado"): la lista de intimaciones/observaciones del expediente con su
// respuesta y la documentación que el partido acompañó, para que la supervisora
// o el Secretario tilden las que quieren volcar al informe.
//
// Al tildar, se llama a onSeleccion con los ítems en ORDEN CRONOLÓGICO (no el
// orden de tildado); el modal que lo contiene los agrega al cuerpo con
// aplicarAntecedentes. Se extrajo acá para que todos los modales de informe del
// sistema (documentación, atributos, TEP, reempadronamiento) lo compartan.
// =============================================================================
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { ItemHistorialInforme } from '../../utils/historialInforme';
import { tipoDocLabels } from '../../utils/tiposDocumento';

interface Props {
  expedienteId: string;
  // Ítems tildados, en orden cronológico. El contenedor arma el bloque.
  onSeleccion: (items: ItemHistorialInforme[]) => void;
  // Al cambiar de valor se limpia la selección (el modal lo usa para resetear
  // cuando se abre con una config/texto nuevo).
  resetKey?: unknown;
}

const fmtFecha = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

export default function PanelHistorialInforme({ expedienteId, onSeleccion, resetKey }: Props) {
  const [historial, setHistorial] = useState<ItemHistorialInforme[]>([]);
  const [cargando, setCargando] = useState(false);
  const [seleccionados, setSeleccionados] = useState<string[]>([]);

  // El endpoint del historial es genérico (sirve para cualquier expediente);
  // quedó bajo /no-presentado porque fue el primer circuito que lo necesitó.
  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    api.get(`/no-presentado/${expedienteId}/historial`)
      .then((res) => { if (!cancelado) setHistorial(res.data.data || []); })
      .catch((err: any) => {
        if (!cancelado) {
          toast.error(err.response?.data?.error?.message || 'No se pudo cargar el historial de observaciones');
        }
      })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [expedienteId]);

  useEffect(() => { setSeleccionados([]); }, [resetKey]);

  const toggleItem = (itemId: string) => {
    setSeleccionados((prev) => {
      const next = prev.includes(itemId) ? prev.filter((s) => s !== itemId) : [...prev, itemId];
      onSeleccion(historial.filter((h) => next.includes(h.id)));
      return next;
    });
  };

  // `subsanada` es el booleano de la observación: lo tilda la Secretaría cuando da
  // por cumplida la respuesta del partido. Es distinto de "respondida" —el partido
  // pudo contestar algo que no alcanza—, y es la separación que importa en el informe.
  const grupos = [
    {
      titulo: 'Sin subsanar',
      tono: 'bg-amber-50 text-amber-800',
      items: historial.filter((h) => !h.subsanada),
    },
    {
      titulo: 'Subsanadas',
      tono: 'bg-green-50 text-green-800',
      items: historial.filter((h) => !!h.subsanada),
    },
  ];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <p className="text-sm font-medium text-gray-700">Intimaciones y observaciones</p>
        <p className="text-xs text-gray-500">
          Tildá las que quieras mostrar.
        </p>
      </div>
      <div className="overflow-y-auto max-h-80">
        {cargando && (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        )}
        {!cargando && historial.length === 0 && (
          <p className="p-3 text-sm text-gray-500">Sin intimaciones ni observaciones registradas.</p>
        )}
        {/* Separadas por SUBSANADA: lo que sigue abierto es lo que el Secretario tiene
            que ver primero — es el motivo por el que el expediente no avanzó. Lo ya
            subsanado va debajo, como antecedente. */}
        {!cargando && grupos.map(({ titulo, items, tono }) => items.length === 0 ? null : (
          <div key={titulo}>
            <p className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide sticky top-0 ${tono}`}>
              {titulo} ({items.length})
            </p>
            <div className="divide-y divide-gray-100">
              {items.map(renderItem)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  function renderItem(item: ItemHistorialInforme) {
    return (
          <label key={item.id} className="flex gap-2 p-3 hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 shrink-0 w-4 h-4"
              checked={seleccionados.includes(item.id)}
              onChange={() => toggleItem(item.id)}
            />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-700">
                {item.tipo === 'INTIMACION' ? 'Intimación' : 'Observación'} · {fmtFecha(item.creadoEn)}
                {item.respuestaTexto
                  ? <span className="ml-1 text-green-700">· respondida</span>
                  : <span className="ml-1 text-red-600">· sin respuesta</span>}
              </p>
              <p className="text-xs text-gray-600 break-words line-clamp-3">{item.texto}</p>
              {!!item.adjuntos?.length && (
                <p className="text-xs text-gray-500 mt-1 break-words">
                  📎 {item.adjuntos.map((a) => tipoDocLabels[a.tipo] || a.tipo).join(', ')}
                </p>
              )}
            </div>
          </label>
    );
  }
}
